// /reports/table-turnover — Table Turnover (PAGES.md §3.52) — RESTAURANT

import { useState, useMemo } from 'react';
import type { ColumnDef, TimeRangeValue } from '@bbpos/ui';
import { HeatmapChart } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportKpiRow,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  useReportFilter,
  useReportQuery,
  reportsApi,
  downloadCsv,
  shareReportLink,
  DEFAULT_REPORT_PRESET,
  KpiIcons,
} from '@/features/reports';
import type { TableTurnoverRow } from '@/features/reports';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';

type Row = TableTurnoverRow & { id: string };

function makeColumns(currency: CurrencyCode): ColumnDef<Row>[] {
  return [
    { key: 'table_number', header: 'Table', render: (_v, row) => <span className="inline-flex h-7 min-w-[2rem] items-center justify-center rounded-md bg-gradient-to-br from-violet-500 to-pink-500 px-2 text-xs font-bold text-white tabular-nums">{row.table_number}</span> },
    { key: 'avg_duration_min', header: 'Avg Duration', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.avg_duration_min.toFixed(0)} min</span> },
    { key: 'turnovers', header: 'Turnovers', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.turnovers.toLocaleString('en-IN')}</span> },
    { key: 'revenue', header: 'Revenue', numeric: true, render: (_v, row) => <span className="text-sm font-semibold tabular-nums">{formatPaise(row.revenue, currency)}</span> },
  ];
}

export function TableTurnoverReportPage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const filter = useReportFilter(timeRange, locationId);
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const csvDecimals = Math.round(Math.log10(subunit));
  const columns = useMemo(() => makeColumns(currency), [currency]);

  const { data, isLoading, error } = useReportQuery({
    key: 'table-turnover',
    filter,
    queryFn: (f) => reportsApi.getTableTurnover(f),
  });

  const rows: Row[] = useMemo(
    () => (data?.tables ?? []).map((t) => ({ ...t, id: t.table_number })),
    [data],
  );

  const heatmapCells = useMemo(
    () =>
      rows.map((t) => ({
        row: `Table ${t.table_number}`,
        col: 'Turnovers',
        value: t.turnovers,
      })),
    [rows],
  );

  const handleExport = (fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      // Browser print dialog -> "Save as PDF"; no server-side PDF renderer exists.
      window.print();
      return;
    }
    if (fmt === 'csv') {
      downloadCsv(
        `table-turnover-${filter.range.from}-to-${filter.range.to}.csv`,
        [
          { header: 'Table', get: (r: Row) => r.table_number },
          { header: 'Avg Duration (min)', get: (r: Row) => r.avg_duration_min.toFixed(0) },
          { header: 'Turnovers', get: (r: Row) => r.turnovers },
          { header: `Revenue (${symbol})`, get: (r: Row) => (r.revenue / subunit).toFixed(csvDecimals) },
        ],
        rows,
      );
      return;
    }
    if (fmt === 'whatsapp' || fmt === 'email') {
      shareReportLink(fmt, 'Orio360 Table Turnover', `Avg duration: ${(data?.avg_duration_min ?? 0).toFixed(0)} min • Revenue/Table: ${formatPaise(data?.revenue_per_table ?? 0, currency)}`);
    }
  };

  return (
    <ReportPageShell
      title="Table Turnover"
      subtitle="Seating duration, turnover rate and revenue per table."
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports', href: '/reports' }, { label: 'Table Turnover' }]}
      accent="violet-pink"
      headerActions={<ReportExportMenu onExport={handleExport} />}
      filterBar={<ReportFilterBar timeRange={timeRange} onTimeRangeChange={setTimeRange} locationId={locationId} onLocationChange={setLocationId} />}
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={3} />
      ) : error ? (
        <ReportError />
      ) : (
        <>
          <ReportKpiRow
            tiles={[
              { id: 'dur', title: 'Avg Duration', value: `${(data?.avg_duration_min ?? 0).toFixed(0)} min`, tone: 'violet', icon: KpiIcons.clock },
              { id: 'rate', title: 'Avg Turnover Rate', value: (data?.avg_turnover_rate ?? 0).toFixed(1), tone: 'pink', icon: KpiIcons.trendUp },
              { id: 'revPerTable', title: 'Revenue / Table', value: formatPaise(data?.revenue_per_table ?? 0, currency), tone: 'brand', icon: KpiIcons.rupee },
            ]}
          />
          {heatmapCells.length > 0 ? (
            <div className="rounded-2xl bg-white dark:bg-neutral-800/80 dark:backdrop-blur-xl shadow-md p-5 mb-6 animate-slide-up">
              <HeatmapChart data={heatmapCells} title="Table Usage Heatmap" rowLabel="Table" colLabel="Metric" />
            </div>
          ) : null}
          <ReportTable title="Per-Table Metrics" columns={columns} data={rows} emptyTitle="No table data for selected period" totalCount={rows.length} />
        </>
      )}
    </ReportPageShell>
  );
}
